import { Feather } from 'lucide-react';
import MarkdownText from '../MarkdownText';

const DEFAULT_LABELS = ['Constat', 'Diagnostic', 'Mise en perspective'];

const LABEL_HUMAN = {
  constat: 'Constat',
  diagnostic: 'Diagnostic',
  perspective: 'Mise en perspective',
  mise_en_perspective: 'Mise en perspective',
  nuance: 'Mise en perspective',
};

function humanizeLabel(label, idx) {
  if (!label) return DEFAULT_LABELS[idx] || null;
  const key = String(label).toLowerCase().replace(/[\s-]+/g, '_');
  return LABEL_HUMAN[key] || label;
}

function ParagraphsBlock({ paragraphs }) {
  // paragraphs : array of strings OR array of {label, text}
  const items = paragraphs.map((p, i) => {
    if (typeof p === 'string') return { label: null, text: p, idx: i };
    return { label: humanizeLabel(p.label, i), text: p.text || '', idx: i };
  }).filter((p) => p.text && p.text.trim());

  if (items.length === 0) return null;

  // If exactly 3 paragraphs without explicit labels, infer the canonical
  // structure (constat / diagnostic / mise en perspective).
  const showLabels = items.length === 3 && items.every((p) => !p.label);
  return (
    <div className="space-y-5" data-testid="senior-analysis-paragraphs">
      {items.map((p, i) => {
        const label = p.label || (showLabels ? DEFAULT_LABELS[i] : null);
        return (
          <div key={i}>
            {label && (
              <div className="text-[9px] text-cabinet-bordeaux uppercase tracking-[0.22em] font-medium mb-1.5">
                {label}
              </div>
            )}
            <p className="text-[14px] text-cabinet-ink leading-[1.8]">{p.text}</p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Bloc d'analyse senior — fond ivoire foncé, liseré gauche bordeaux 3px,
 * tag "Analyse senior · Cabinet Benchory".
 *
 * Dégradation gracieuse en 3 niveaux :
 *   1. content_data.paragraphs (array structuré ou string array) → titres
 *      + paragraphes propres, pluriel auto.
 *   2. content_md → MarkdownText.
 *   3. fallback élégant si aucune des deux.
 */
export default function SeniorAnalysis({ section }) {
  const data = section?.content_data || {};
  const md = section?.content_md;

  let body;
  if (Array.isArray(data.paragraphs) && data.paragraphs.length > 0) {
    body = <ParagraphsBlock paragraphs={data.paragraphs} />;
  } else if (md && md.trim()) {
    body = <MarkdownText text={md} className="text-[14px] text-cabinet-ink leading-[1.8]" />;
  } else {
    body = (
      <p className="font-playfair italic text-[13px] text-cabinet-ink-soft leading-[1.85]">
        L'analyse senior de cette famille sera disponible dès la finalisation du prompt système maître et la génération complète du rapport.
      </p>
    );
  }

  return (
    <div
      className="bg-cabinet-cream-dark border-l-[3px] border-l-cabinet-bordeaux border-y border-r border-cabinet-border p-7"
      data-testid="senior-analysis"
    >
      <div className="flex items-center gap-2 mb-5">
        <Feather size={13} className="text-cabinet-bordeaux" />
        <span className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.25em] font-medium">
          Analyse senior · Cabinet Benchory
        </span>
      </div>
      {body}
    </div>
  );
}
